// Is there any need to convert the $fr from the front end? If there is a REGEX check in place, is that enough to make sure things work.
import tradeDataStore from "@/store/tradeDataStore";

import { generateToastMessageForTradeFlowErrors } from "../notifications/toastNotificationHelpers";
import { ISwapTradeFees } from "../interfaces and Enums/InterfaceExecutionStrategy";

export const ruleOfEights = 0.00390625;
export const ruleOfQuarters = 0.0078125;
export const ruleOfHalves = 0.015625;
export const ruleOfWhole = 0.03125;

export function containsHyphenInQuote(quoteString: string): boolean {
  let stringContainsHyphen = false;
  // If the value is a negative, need to remove the - from the start of the string otherwise the negative symbol will be treated as a $fr.
  if (quoteString[0] == "-") {
    const quoteStringTrimmed = quoteString.substring(1);
    stringContainsHyphen = quoteStringTrimmed.includes("-");
    return stringContainsHyphen;
  }
  // If the value is NOT negative, return the result of a - check on the whole string.
  stringContainsHyphen = quoteString.includes("-");
  return stringContainsHyphen;
}

export function returnFractionString(
  dollarFraction: string,
  reqestWholeNumber: boolean = true,
) {
  let splits = null;
  if (dollarFraction[0] == "-") {
    // If the value is a negative, need to make sure the - is removed to run the split of the second - rather than the negavie symbol.
    splits = dollarFraction.substring(1).split("-");
  } else {
    splits = dollarFraction.split("-");
  }

  const valueBeforeHyphen = splits?.length > 0 ? splits[0] : "0";
  const valueAfterHyphen = splits?.length > 1 ? splits[1] : "0";

  if (reqestWholeNumber) {
    return valueBeforeHyphen;
  } else {
    return valueAfterHyphen;
  }
}

/**
 * Converts a dollar fraction string back to its decimal equivalent.
 * E.g., "90-12" → 90 + 12/32 = 90.375
 *        "99-163" → 99 + 16/32 + 3*(1/256) = 99.51171875
 *        "99-16+" → 99 + 16/32 + 4*(1/256) = 99.515625
 *
 * @param fractionString - Dollar fraction string (e.g., "90-12", "99-163", "99-16+")
 * @returns The decimal equivalent
 */
export function dollarFractionToDecimal(fractionString: string): number {
  const isNegative = fractionString[0] === "-";
  const wholeStr = returnFractionString(fractionString, true);
  const fractionPart = returnFractionString(fractionString, false);

  const whole = parseInt(wholeStr, 10) || 0;

  // Handle the fraction part: first 2 digits are 32nds, optional 3rd digit is 8ths of 32nds
  let thirtySeconds = 0;
  let zValue = 0;

  if (fractionPart.endsWith("+")) {
    // "+" means 4/8 of a 32nd
    thirtySeconds = parseInt(fractionPart.substring(0, fractionPart.length - 1), 10) || 0;
    zValue = 4;
  } else if (fractionPart.length === 3) {
    // Third digit is the Z-value (1/8 of a 32nd)
    thirtySeconds = parseInt(fractionPart.substring(0, 2), 10) || 0;
    zValue = parseInt(fractionPart[2], 10) || 0;
  } else {
    thirtySeconds = parseInt(fractionPart, 10) || 0;
  }

  const decimalValue = whole + thirtySeconds / 32 + zValue / 256;
  return isNegative ? -decimalValue : decimalValue;
}

// If the user enters a number - need to see if it can be converted into a fraction.
export function convertToDollarFraction(value: number) {
  let result = "";
  const wholeNumber = Math.floor(value);

  if (wholeNumber == value) {
    result = `${wholeNumber}-00`;
  } else {
    const fractionNumber = value - wholeNumber;
    const thirty2nd = 1 / 32;
    const thirdDigit8th = (1 / 8) * (1 / 32);

    if (Math.abs(fractionNumber % thirdDigit8th) < Number.EPSILON) {
      let fractionNumber1 = fractionNumber / thirty2nd;
      if (fractionNumber1 == Math.floor(fractionNumber1)) {
        result = `${wholeNumber}-${fractionNumber1.toString().padStart(2, "0")}`;
      } else {
        let fractionNumber2 = fractionNumber1 - Math.floor(fractionNumber1);
        fractionNumber1 = Math.floor(fractionNumber1);
        fractionNumber2 = fractionNumber2 * 8;
        let fractionString = formatFraction(fractionNumber1, fractionNumber2);
        if (fractionString.length == 3 && fractionString[2] == "4") {
          fractionString = fractionString.substring(0, 2) + "+";
        }
        // Front end only code to change single number post hyphen displays to 2 chars.
        if (fractionString.toString().length == 0) {
          fractionString = `0${fractionString}`;
        }
        result = `${wholeNumber}-${fractionString}`;
      }
    } else {
      // just return result as decimal number because it cannot be expressed as $fr
      result = value.toString();
    }
  }
  return result;
}

const formatFraction = (
  fractionNumber1: number,
  fractionNumber2: number,
): string => {
  const formatted1 = fractionNumber1.toString().padStart(2, "0"); // Ensure 2 digits
  const formatted2 = fractionNumber2.toString(); // No padding needed
  return `${formatted1}${formatted2}`;
};

export function regexCheckFractionQuote(params: any): boolean {
  // LQC-890: allow optional leading "-" so negatives save as negative;
  // LQC-889 flashes them on quote/execute.
  const regex = /^-?\d+(\.\d+)?$|^-?\d+-\d{1,3}$|^-?\d+-\d{2}\+$/;
  if (regex.test(params.newValue)) {
    return true;
  }
  if (params.newValue != "") {
    generateToastMessageForTradeFlowErrors({
      action: "dollarFRQuote",
      outcome: "incorrectValue",
    });
  }
  return false; // Reject invalid input
}

// const isNullOrWhiteSpace = (stringQuote: string | null | undefined): boolean => {
//   return !stringQuote || stringQuote.trim().length === 0;
// };

// // Write function to handle converting the string into a decimal. Will need to convert strings to decimal then back to a string if the user enters a string.
// function convertToDecimal(quotedStringFraction: string, tickSize: number = 0) {
//   let wholeNumber = returnFractionString(quotedStringFraction);
//   let valueAfterHyphen = returnFractionString(quotedStringFraction, false);

//   let result = 0;
//   let fractionAsDecimal = 0;
// }

export function formatDollarFractionString(quotedStringFraction: string) {
  if (quotedStringFraction.includes(".")) {
    return quotedStringFraction;
  }

  // LQC-890: preserve leading "-" so negative fraction values keep their sign
  // (returnFractionString strips it for splitting purposes).
  const isNegative = quotedStringFraction[0] === "-";
  const wholeNumber = returnFractionString(quotedStringFraction);
  let valueAfterHyphen = returnFractionString(quotedStringFraction, false);

  if (valueAfterHyphen.length == 1) {
    valueAfterHyphen = "0" + valueAfterHyphen;
  }
  // NEED SWITCH STATEMENT FOR TICK SIZE / RULES for more than 2 chars at the hyphen.
  const sign = isNegative ? "-" : "";
  return `${sign}${wholeNumber}-${valueAfterHyphen}`;
}

/**
 * Converts a dollar fraction Z-value digit to a Unicode fraction character for plain-text display (e.g., input fields).
 * E.g., "116-023" → "116-02⅜", "116-034" → "116-03+"
 */
export function formatDollarFractionWithZValueText(
  quotedStringFraction: string,
): string {
  const formatted = formatDollarFractionString(quotedStringFraction);
  const valueAfterHyphen = returnFractionString(quotedStringFraction, false);

  if (valueAfterHyphen.length !== 3) {
    return formatted;
  }

  const zDigit = valueAfterHyphen[2];
  const zToUnicode: Record<string, string> = {
    "1": "⅛",
    "2": "¼",
    "3": "⅜",
    "4": "+",
    "5": "⅝",
    "6": "¾",
    "7": "⅞",
  };

  return `${formatted.slice(0, -1)}${zToUnicode[zDigit] || zDigit}`;
}

export function formatDollarFractionZValueForSmallFractionDisplay(
  quotedStringFraction: string,
  tickSize?: number,
) {
  // CHECK all places where tickSize gets updated in the code. DynamicCellEditorQuoteDollarFraction, formatDollarFractionZValueForSmallFractionDisplay
  const tickSizeToUse = tickSize != null ? tickSize : ruleOfEights;

  // let wholeNumber = returnFractionString(quotedStringFraction);
  const valueAfterHyphen = returnFractionString(quotedStringFraction, false);

  // Need to change to false to start.
  let isValidTickSize = true;
  let numerator = 1;
  let denominator = 8;

  let zValueHTML = <span></span>;

  if (valueAfterHyphen.length == 3) {
    const zValue = valueAfterHyphen.slice(-1);

    if (tickSizeToUse == ruleOfEights) {
      if (zValue == "1") {
        numerator = 1;
        denominator = 8;
      } else if (zValue == "2") {
        numerator = 1;
        denominator = 4;
      } else if (zValue == "3") {
        numerator = 3;
        denominator = 8;
      } else if (zValue == "4" || zValue == "+") {
        zValueHTML = <span>+</span>;
        isValidTickSize = false;
      } else if (zValue == "5") {
        numerator = 5;
        denominator = 8;
      } else if (zValue == "6") {
        numerator = 3;
        denominator = 4;
      } else if (zValue == "7") {
        numerator = 7;
        denominator = 8;
      } else {
        // Should never happen as 8 and 9 unable to be entered with REGEX rules.
        zValueHTML = <span></span>;
        isValidTickSize = false;
      }
    } else if (tickSizeToUse == ruleOfQuarters) {
      if (zValue == "1" || zValue == "2") {
        numerator = 1;
        denominator = 4;
      } else if (zValue == "3" || zValue == "4" || zValue == "+") {
        zValueHTML = <span>+</span>;
        isValidTickSize = false;
      } else if (zValue == "5" || zValue == "6") {
        numerator = 3;
        denominator = 4;
      } else {
        // Should never happen as 8 and 9 unable to be entered with REGEX rules.
        zValueHTML = <span></span>;
        isValidTickSize = false;
      }
      // isValidTickSize = true;
    } else if (tickSizeToUse == ruleOfHalves) {
      if (
        zValue == "2" ||
        zValue == "3" ||
        zValue == "4" ||
        zValue == "+" ||
        zValue == "5"
      ) {
        zValueHTML = <span>+</span>;
        isValidTickSize = false;
      } else {
        // Should never happen as 8 and 9 unable to be entered with REGEX rules.
        zValueHTML = <span></span>;
        isValidTickSize = false;
      }
      // isValidTickSize = true;
    } else {
      isValidTickSize = false;
    }
  }

  // NEED SWITCH STATEMENT FOR TICK SIZE / RULES for more than 2 chars at the hyphen.
  if (isValidTickSize) {
    return (
      <span>
        <sup>{numerator}</sup>&frasl;<sub>{denominator}</sub>
      </span>
    );
  }

  return zValueHTML;
}

// Create 2 functions that return the requierd values for the fraction conversions.
export function returnFractionStringQuoteValue(
  isOutright: boolean,
  params: any,
) {
  // let ruleToUse = ruleOfEights;

  let quoteDisplayValue = params.value;

  // if (isOutright) {
  //   // if executionRequestCounterParties it's looking from the OP perspective.
  //   if (params.data.executionRequestCounterParties != undefined) {
  //     if (
  //       params.data.executionRequestCounterParties[0] &&
  //       params.data.executionRequestCounterParties[0].legs[0].tickSize != null
  //     ) {
  //       ruleToUse =
  //         params.data.executionRequestCounterParties[0].legs[0].tickSize;
  //     }
  //   } else if (params.data.legs[0].tickSize != null) {
  //     ruleToUse = params.data.legs[0].tickSize;
  //   }
  // } else {
  //   if (params.data.tickSize != null) {
  //     ruleToUse = params.data.tickSize;
  //   }
  // }

  const valueAfterHyphen = returnFractionString(params.value, false);
  if (valueAfterHyphen.length == 3) {
    quoteDisplayValue = quoteDisplayValue.slice(0, -1);
  }
  return quoteDisplayValue;
}

// ZDecimal
export function returnFractionStringZValueDecimal(
  isOutright: boolean,
  params: any,
) {
  const ruleToUse = ruleOfEights; //15063 - Now always to use the ruleOFEights
  let zValueDecimal = <span></span>;

  //15063 - No longer going to look for the tickSize off the block - Always using the ruleOFEights
  // if (isOutright) {
  //   if (params.data.executionRequestCounterParties != undefined) {
  //     if (
  //       params.data.executionRequestCounterParties[0] &&
  //       params.data.executionRequestCounterParties[0].legs[0].tickSize != null
  //     ) {
  //       ruleToUse =
  //         params.data.executionRequestCounterParties[0].legs[0].tickSize;
  //     }
  //   } else if (
  //     params.data.status != ExecutionStrategyStatus.New &&
  //     params.data.legs[0].tickSize != null
  //   ) {
  //     ruleToUse = params.data.legs[0].tickSize;
  //   }
  // } else {
  //   if (params.data.tickSize != null) {
  //     ruleToUse = params.data.tickSize;
  //   }
  // }

  const valueAfterHyphen = returnFractionString(params.value, false);
  if (valueAfterHyphen.length == 3) {
    // Return symbol to add to display.
    zValueDecimal = formatDollarFractionZValueForSmallFractionDisplay(
      params.value,
      ruleToUse,
    );
  }
  return zValueDecimal;
}

export function updateFieldOnParamsToQuote(
  esid: string,
  props: any,
  field: string,
  value: string | number | undefined | ISwapTradeFees[],
) {
  const currentTradeData = tradeDataStore.getState().tradeData;
  const tradeToUpdate = {
    ...currentTradeData.find((trade) => trade.esid == esid),
  };
  if (tradeToUpdate.legs) {
    // Will always have legs as it's only called at the ER.
    tradeToUpdate.legs = tradeToUpdate.legs.map((leg: any) => {
      if (leg.blockId == props.data.blockId) {
        const newLeg = { ...props.data };
        newLeg[field] = value;
        return newLeg;
      } else {
        return { ...leg };
      }
    });
  }
  return tradeToUpdate;
}
